import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormLabel,
	IconButton,
	Radio,
	RadioGroup,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { type FC, useEffect, useState } from "react";
import type { SurveyQuestion, SurveySettings } from "./staffSatisfactionTypes";

interface SurveySettingsModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (settings: SurveySettings) => void;
	currentSettings: SurveySettings;
	allQuestions: SurveyQuestion[];
}

export const SurveySettingsModal: FC<SurveySettingsModalProps> = ({ open, onClose, onSave, currentSettings, allQuestions }) => {
	const [localSettings, setLocalSettings] = useState<SurveySettings>(currentSettings);

	useEffect(() => {
		if (open) setLocalSettings(currentSettings);
	}, [open, currentSettings]);

	const handleQuestionToggle = (questionId: string) => {
		setLocalSettings((prev) => ({
			...prev,
			activeQuestionIds: prev.activeQuestionIds.includes(questionId)
				? prev.activeQuestionIds.filter((id) => id !== questionId)
				: [...prev.activeQuestionIds, questionId],
		}));
	};

	const handleSave = () => {
		onSave(localSettings);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			slotProps={{
				paper: { sx: { borderRadius: 3 } },
			}}
		>
			<DialogTitle sx={{ m: 0, p: 3, pb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Typography
					variant="h5"
					sx={{
						fontWeight: "bold",
					}}
				>
					{"Survey Settings"}
				</Typography>
				<IconButton aria-label="close" onClick={onClose} sx={{ color: "text.secondary" }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ p: 3 }}>
				<Box sx={{ mb: 4 }}>
					<Typography
						variant="h6"
						gutterBottom
						sx={{
							fontWeight: 600,
						}}
					>
						{"Distribution"}
					</Typography>

					<FormControl component="fieldset" sx={{ width: "100%", mb: 3 }}>
						<FormLabel component="legend" sx={{ mb: 1, color: "text.primary", fontWeight: 500 }}>
							{"Staff Targeting"}
						</FormLabel>
						<RadioGroup
							value={localSettings.sendToAllStaff ? "all" : "random"}
							onChange={(e) => setLocalSettings((prev) => ({ ...prev, sendToAllStaff: e.target.value === "all" }))}
						>
							<FormControlLabel value="all" control={<Radio />} label="Send to all staff" />
							<FormControlLabel value="random" control={<Radio />} label="Randomize staff selection" />
						</RadioGroup>
					</FormControl>

					<FormControl component="fieldset" sx={{ width: "100%" }}>
						<FormLabel component="legend" sx={{ mb: 1, color: "text.primary", fontWeight: 500 }}>
							{"Question Selection"}
						</FormLabel>
						<RadioGroup
							value={localSettings.sendAllQuestions ? "all" : "random"}
							onChange={(e) => setLocalSettings((prev) => ({ ...prev, sendAllQuestions: e.target.value === "all" }))}
						>
							<FormControlLabel value="all" control={<Radio />} label="Send all active questions" />
							<FormControlLabel value="random" control={<Radio />} label="Send a random selection of questions" />
						</RadioGroup>
						{!localSettings.sendAllQuestions && (
							<Box sx={{ ml: 4, mt: 1 }}>
								<TextField
									type="number"
									label="Number of questions"
									size="small"
									value={localSettings.randomQuestionCount}
									onChange={(e) =>
										setLocalSettings((prev) => ({
											...prev,
											randomQuestionCount: Math.max(1, parseInt(e.target.value) || 1),
										}))
									}
									slotProps={{ htmlInput: { min: 1, max: localSettings.activeQuestionIds.length } }}
									sx={{ width: 200 }}
								/>
							</Box>
						)}
					</FormControl>
				</Box>

				<Divider sx={{ my: 3 }} />

				<Box>
					<Typography
						variant="h6"
						gutterBottom
						sx={{
							fontWeight: 600,
						}}
					>
						{"Active Questions"}
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "text.secondary",
							mb: 2,
						}}
					>
						{"Select which questions are currently in the rotation to be sent to staff."}
					</Typography>
					<FormGroup>
						{allQuestions.map((question) => (
							<Box
								key={question.id}
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									py: 1.5,
									borderBottom: "1px solid",
									borderColor: "divider",
									"&:last-child": { borderBottom: "none" },
									opacity: 1,
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 2, flexGrow: 1 }}>
									<Typography variant="body2">{question.text}</Typography>
								</Box>
								<Switch checked={localSettings.activeQuestionIds.includes(question.id)} onChange={() => handleQuestionToggle(question.id)} color="primary" />
							</Box>
						))}
					</FormGroup>
				</Box>
			</DialogContent>
			<DialogActions sx={{ p: 3, pt: 2 }}>
				<Button onClick={onClose} color="inherit" sx={{ fontWeight: 600 }}>
					{"Cancel"}
				</Button>
				<Button onClick={handleSave} variant="contained" color="primary" sx={{ fontWeight: 600, px: 3 }}>
					{"Save Settings"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
